/**
 * Corti Integration Service
 *
 * Centralized service for handling Corti transcription API integration.
 * Provides comprehensive audio transcription, fact extraction, and SOAP notes generation.
 * Includes robust error handling, CORS management, and fallback behavior.
 */

import { cortiLogger } from "../../../utils/cortiLogger";
import { getSupabase } from "../../../components/supabaseClient";
import { supabaseUrl, BASE_URLS } from "../../../components/env";

export class CortiIntegrationService {
  constructor() {
    this.apiBaseUrl = this._getBackendUrl();
    this.cortiApiBaseUrl = "/corti/transcribe-recorded/";
    this.retryAttempts = 3;
    this.retryDelay = 2000; // 2 seconds
    this.cortiAvailable = true; // Track service availability
    this.cortiHealthCheckInterval = 30000; // 30 seconds
    this.lastHealthCheck = 0;
    this.fallbackEnabled = true; // Enable fallback when service is unavailable
  }

  /**
   * Check if Corti service is available
   * @returns {Promise<boolean>} Service availability status
   */
  async checkServiceHealth() {
    const now = Date.now();

    // Skip if we checked recently
    if (now - this.lastHealthCheck < this.cortiHealthCheckInterval) {
      return this.cortiAvailable;
    }

    this.lastHealthCheck = now;

    try {
      // Simple health check endpoint
      const healthUrl = `${this.apiBaseUrl}/corti/health`;
      const response = await fetch(healthUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${await this._getAuthToken()}`,
        },
      });

      this.cortiAvailable = response.ok;
      return this.cortiAvailable;
    } catch (error) {
      console.warn("Corti service health check failed:", error.message);
      this.cortiAvailable = false;
      return false;
    }
  }

  /**
   * Transcribe audio file using Corti API with fallback support
   * @param {string} audioUrl - URL of the audio file to transcribe
   * @param {object} patientContext - Patient context information
   * @param {function} onProgress - Progress callback function
   * @returns {Promise<object>} Transcription results
   */
  async transcribeAudioFile(audioUrl, patientContext, onProgress = null) {
    const startTime = Date.now();
    let lastProgressUpdate = 0;

    try {
      this._logProgress(
        onProgress,
        "starting",
        "Initializing transcription..."
      );

      // Validate inputs
      if (!audioUrl) {
        throw new Error("Audio URL is required");
      }

      if (!patientContext || !patientContext.id) {
        throw new Error("Patient context is required");
      }

      this._logProgress(onProgress, "validating", "Validating inputs...");

      // Check service health
      const serviceAvailable = await this.checkServiceHealth();

      if (!serviceAvailable && this.fallbackEnabled) {
        console.log("🔄 Corti service unavailable, using fallback mode");
        return await this._transcribeWithFallback(
          audioUrl,
          patientContext,
          onProgress
        );
      }

      // Prepare form data
      const formData = new FormData();

      // Handle URL-based transcription
      if (audioUrl.startsWith("http")) {
        // Create a text file containing the URL
        const urlBlob = new Blob([audioUrl], { type: "text/plain" });
        formData.append("audio_file", urlBlob, "audio_url.txt");
      } else {
        throw new Error(
          "Only URL-based transcription is supported in this service"
        );
      }

      this._logProgress(
        onProgress,
        "uploading",
        "Sending audio to Corti API..."
      );

      // Make API call with retry logic
      let lastError;
      for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
        try {
          this._logProgress(
            onProgress,
            "transcribing",
            `Transcribing audio (attempt ${attempt}/${this.retryAttempts})...`
          );

          // Create fetch options with CORS handling
          const fetchOptions = {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${await this._getAuthToken()}`,
            },
            // Add timeout and CORS handling
            signal: AbortSignal.timeout(300000), // 5 minute timeout for long recordings
          };

          const response = await fetch(
            this.apiBaseUrl + this.cortiApiBaseUrl,
            fetchOptions
          );

          // Handle CORS errors specifically
          if (response.type === "opaque") {
            throw new Error(
              "CORS_POLICY_ERROR: Request blocked by CORS policy"
            );
          }

          if (!response.ok) {
            // Handle CORS errors in response
            if (response.status === 0 || response.status === 403) {
              throw new Error(
                "CORS_POLICY_ERROR: Cross-origin request blocked"
              );
            }

            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.error ||
                `HTTP ${response.status}: ${response.statusText}`
            );
          }

          const result = await response.json();

          if (result.status !== "success") {
            throw new Error(result.error || "Transcription failed");
          }

          // Process the successful result
          this._logProgress(
            onProgress,
            "processing",
            "Processing transcription results..."
          );

          const processedResults = await this.processTranscriptionResults(
            result,
            patientContext
          );

          // Log successful transcription
          const processingTime = (Date.now() - startTime) / 1000;
          await this._logTranscription({
            patientContext,
            audioUrl,
            result: processedResults,
            processingTime,
            attempt,
          });

          this._logProgress(
            onProgress,
            "completed",
            "Transcription completed successfully!"
          );

          return {
            success: true,
            ...processedResults,
            processingTime,
            attempts: attempt,
            mode: "corti_api",
          };
        } catch (error) {
          lastError = error;

          // Check for CORS errors
          if (
            error.message.includes("CORS_POLICY_ERROR") ||
            (error.name === "TypeError" && error.message.includes("fetch"))
          ) {
            console.warn("CORS error detected:", error.message);
            this.cortiAvailable = false;

            // Try fallback if enabled
            if (this.fallbackEnabled) {
              console.log("🔄 CORS error, attempting fallback transcription");
              return await this._transcribeWithFallback(
                audioUrl,
                patientContext,
                onProgress
              );
            }
          }

          console.warn(
            `Corti transcription attempt ${attempt} failed:`,
            error.message
          );

          if (attempt < this.retryAttempts) {
            this._logProgress(
              onProgress,
              "retrying",
              `Retrying in ${this.retryDelay / 1000} seconds...`
            );
            await this._delay(this.retryDelay * attempt); // Exponential backoff
          }
        }
      }

      // All attempts failed
      throw new Error(
        `Transcription failed after ${this.retryAttempts} attempts: ${lastError.message}`
      );
    } catch (error) {
      console.error("Corti transcription error:", error);

      // Log failed transcription
      const processingTime = (Date.now() - startTime) / 1000;
      await this._logTranscription({
        patientContext,
        audioUrl,
        error: error.message,
        processingTime,
      });

      this._logProgress(
        onProgress,
        "error",
        `Transcription failed: ${this._getUserFriendlyError(error.message)}`
      );

      return {
        success: false,
        error: this._getUserFriendlyError(error.message),
        processingTime,
        mode: "failed",
      };
    }
  }

  /**
   * Fallback transcription when Corti service is unavailable
   * @param {string} audioUrl - Audio URL
   * @param {object} patientContext - Patient context
   * @param {function} onProgress - Progress callback
   * @returns {Promise<object>} Fallback results
   */
  async _transcribeWithFallback(audioUrl, patientContext, onProgress) {
    try {
      console.log("🔄 Using fallback transcription method");

      this._logProgress(
        onProgress,
        "fallback",
        "Corti service unavailable, using local transcription..."
      );

      // Create fallback transcript data
      const fallbackTranscript = [
        {
          transcript:
            "Audio transcription temporarily unavailable. Please try again later or contact support.",
          speaker: "system",
          timestamp: Date.now(),
          confidence: 0.5,
        },
      ];

      // Generate continuous transcript for fallback mode
      const fallbackContinuousTranscript =
        this._createContinuousTranscript(fallbackTranscript);

      // Create a simple fallback result
      const fallbackResult = {
        transcript: fallbackTranscript,
        continuous_transcript: fallbackContinuousTranscript, // Add continuous transcript for fallback mode
        facts: [
          {
            text: "Audio file provided but transcription service is temporarily unavailable",
            category: "system_status",
            confidence: 1.0,
            source: "fallback",
          },
        ],
        soap_notes: {
          transcript_only:
            "Subjective:\nAudio recording received but transcription service unavailable.\n\nObjective:\nUnable to process audio due to service unavailability.\n\nAssessment:\nSystem maintenance or service disruption.\n\nPlan:\nPlease try again later or contact technical support.",
          facts_only:
            "Subjective:\n--\n\nObjective:\n--\n\nAssessment:\nService temporarily unavailable\n\nPlan:\nContact support if issue persists",
        },
        interaction_id: `fallback_${Date.now()}`,
        credit_usage: 0, // No credits used in fallback
        processing_method: "fallback",
        audio_storage_url: audioUrl,
        metadata: {
          processed_at: new Date().toISOString(),
          patient_id: patientContext.id,
          patient_name: patientContext.name || "Unknown",
          fallback_reason: "corti_service_unavailable",
          original_url: audioUrl,
        },
      };

      // Log fallback usage
      const processingTime = (Date.now() - Date.now()) / 1000; // Instant fallback
      await this._logTranscription({
        patientContext,
        audioUrl,
        result: fallbackResult,
        processingTime,
        attempt: 1,
      });

      this._logProgress(
        onProgress,
        "completed",
        "Fallback transcription completed"
      );

      return {
        success: true,
        ...fallbackResult,
        processingTime: 0.1,
        attempts: 1,
        mode: "fallback",
      };
    } catch (fallbackError) {
      console.error("Fallback transcription also failed:", fallbackError);

      return {
        success: false,
        error:
          "Both Corti service and fallback are unavailable. Please try again later.",
        processingTime: 0,
        mode: "failed",
      };
    }
  }

  /**
   * Get user-friendly error message
   * @param {string} errorMessage - Raw error message
   * @returns {string} User-friendly error message
   */
  _getUserFriendlyError(errorMessage) {
    if (
      errorMessage.includes("CORS_POLICY_ERROR") ||
      errorMessage.includes("blocked by CORS policy")
    ) {
      return "Unable to connect to transcription service due to browser security restrictions. Please check your network connection and try again.";
    }

    if (errorMessage.includes("fetch") || errorMessage.includes("network")) {
      return "Network connection failed. Please check your internet connection and try again.";
    }

    if (errorMessage.includes("timeout")) {
      return "The transcription is taking too long. Please try with a shorter audio file.";
    }

    if (
      errorMessage.includes("HTTP 500") ||
      errorMessage.includes("Internal Server Error")
    ) {
      return "The transcription service is temporarily unavailable. Please try again in a few minutes.";
    }

    if (
      errorMessage.includes("HTTP 401") ||
      errorMessage.includes("Unauthorized")
    ) {
      return "Authentication failed. Please log out and log back in.";
    }

    if (
      errorMessage.includes("HTTP 403") ||
      errorMessage.includes("Forbidden")
    ) {
      return "Access denied. Please check your account permissions.";
    }

    return "An error occurred during transcription. Please try again or contact support if the problem persists.";
  }

  /**
   * Process and clean transcription results with database schema fixes
   * @param {object} rawResults - Raw results from Corti API
   * @param {object} patientContext - Patient context information
   * @returns {object} Processed results
   */
  async processTranscriptionResults(rawResults, patientContext) {
    try {
      console.log("🔍 Processing transcription results:", rawResults);

      // DEBUG: Log the raw data structure to identify mapping issues
      console.log("🔍 RAW DATA ANALYSIS:");
      console.log(
        `📝 Raw transcript type: ${typeof rawResults.transcript}, isArray: ${Array.isArray(
          rawResults.transcript
        )}`
      );
      console.log(
        `📝 Raw transcript sample:`,
        rawResults.transcript?.slice(0, 2) || "N/A"
      );
      console.log(
        `📊 Raw facts type: ${typeof rawResults.facts}, isArray: ${Array.isArray(
          rawResults.facts
        )}`
      );
      console.log(
        `📊 Raw facts sample:`,
        rawResults.facts?.slice(0, 2) || "N/A"
      );

      // Extract and validate basic data
      const transcript = rawResults.transcript || [];
      const rawFacts = rawResults.facts || [];
      const soapNotes = this._cleanSoapNotes(rawResults.soap_notes || {});

      // FIX: Transform facts from array of strings to array of objects
      const facts = Array.isArray(rawFacts)
        ? rawFacts.map((fact, index) => {
            if (typeof fact === "string") {
              return {
                text: fact,
                category: "general", // Default category
                confidence: 1.0,
                source: "extraction"
              };
            } else if (typeof fact === "object" && fact !== null) {
              return {
                text: fact.text || fact.fact || "",
                category: fact.category || fact.group || "general",
                confidence: fact.confidence || 1.0,
                source: fact.source || "extraction"
              };
            }
            return {
              text: String(fact || ""),
              category: "general",
              confidence: 1.0,
              source: "extraction"
            };
          })
        : [];

      // FIX: Transform transcript from array of strings to array of objects
      // Backend returns transcript as array of strings, but frontend expects objects
      const normalizedTranscript = Array.isArray(transcript)
        ? transcript.map((item, index) => {
            // If item is already an object with transcript/text property, use it
            if (
              typeof item === "object" &&
              item !== null &&
              (item.transcript || item.text)
            ) {
              return {
                transcript: item.transcript || item.text || "",
                text: item.text || item.transcript || "",
                speaker: item.speaker || "unknown",
                timestamp: item.timestamp || Date.now() + index * 1000,
                confidence: item.confidence || 1.0,
              };
            }
            // If item is a string, convert it to object format
            else if (typeof item === "string") {
              return {
                transcript: item,
                text: item,
                speaker: "unknown",
                timestamp: Date.now() + index * 1000,
                confidence: 1.0,
              };
            }
            // Fallback for unexpected formats
            else {
              return {
                transcript: String(item || ""),
                text: String(item || ""),
                speaker: "unknown",
                timestamp: Date.now() + index * 1000,
                confidence: 1.0,
              };
            }
          })
        : [];

      // DEBUG: Log the normalized data
      console.log("🔍 NORMALIZED DATA ANALYSIS:");
      console.log(
        `✅ Normalized transcript length: ${normalizedTranscript.length}`
      );
      console.log(
        `✅ Normalized transcript sample:`,
        normalizedTranscript.slice(0, 2)
      );
      console.log(`✅ Facts length: ${facts.length}`);
      console.log(`✅ Facts sample:`, facts.slice(0, 2));

      // Validate transcript
      const validatedTranscript =
        this._validateTranscript(normalizedTranscript);

      // Extract facts from transcript if not provided
      const extractedFacts =
        facts.length > 0
          ? facts
          : await this.extractFactsFromTranscript(validatedTranscript);

      // Generate SOAP notes if not provided
      const generatedSoapNotes =
        Object.keys(soapNotes).length > 0
          ? soapNotes
          : await this.generateSOAPNotes(validatedTranscript, extractedFacts);

      // Fix credit_usage for database compatibility (convert float to integer)
      let creditUsage = rawResults.credit_usage || 0;
      if (typeof creditUsage === "string") {
        creditUsage = parseFloat(creditUsage);
      }
      if (typeof creditUsage === "number" && !Number.isInteger(creditUsage)) {
        creditUsage = Math.round(creditUsage);
      }
      creditUsage = parseInt(creditUsage) || 0;

      // Create continuous transcript text for display purposes
      const continuousTranscript =
        this._createContinuousTranscript(validatedTranscript);

      // DEBUG: Log continuous transcript generation
      console.log("🔍 DEBUG - Continuous transcript generation:");
      console.log(
        `📝 Input transcript segments: ${validatedTranscript.length}`
      );
      console.log(
        `📝 Generated continuous transcript: "${continuousTranscript.substring(
          0,
          100
        )}..."`
      );
      console.log(
        `📝 Continuous transcript length: ${continuousTranscript.length}`
      );
      console.log(
        `📝 Continuous transcript type: ${typeof continuousTranscript}`
      );

      // Process and structure the results
      const processedResults = {
        transcript: validatedTranscript,
        continuous_transcript: continuousTranscript, // Single continuous text version
        facts: extractedFacts,
        soap_notes: generatedSoapNotes,
        interaction_id: rawResults.interaction_id || "",
        credit_usage: creditUsage, // Now guaranteed to be integer
        processing_method: rawResults.processing_method || "rest_api",
        audio_storage_url: rawResults.audio_storage_url || null,
        metadata: {
          processed_at: new Date().toISOString(),
          patient_id: patientContext.id,
          patient_name:
            patientContext.name ||
            patientContext[Object.keys(patientContext)[0]],
          original_result_keys: Object.keys(rawResults),
          fact_count: extractedFacts.length,
          soap_notes_available: Object.keys(generatedSoapNotes).length > 0,
          transcript_segments: validatedTranscript.length,
        },
      };

      // DEBUG: Log the final processed results
      console.log("🔍 FINAL PROCESSED RESULTS:");
      console.log(
        `🎯 Final transcript length: ${processedResults.transcript.length}`
      );
      console.log(
        `🎯 Final transcript sample:`,
        processedResults.transcript.slice(0, 2)
      );
      console.log(`🎯 Final facts length: ${processedResults.facts.length}`);
      console.log(`🎯 Final facts sample:`, processedResults.facts.slice(0, 2));
      console.log(
        `🎯 Continuous transcript field present: ${
          processedResults.continuous_transcript ? "YES" : "NO"
        }`
      );
      console.log(
        `🎯 Continuous transcript length: ${
          processedResults.continuous_transcript?.length || 0
        }`
      );

      console.log("✅ Transcription results processed:", processedResults);
      return processedResults;
    } catch (error) {
      console.error("❌ Error processing transcription results:", error);
      throw new Error(
        `Failed to process transcription results: ${error.message}`
      );
    }
  }

  /**
   * Save transcription results to database with improved error handling
   * @param {string} patientId - Patient ID
   * @param {object} results - Processed transcription results
   * @returns {Promise<object>} Save result
   */
  async saveTranscriptionResults(patientId, results) {
    try {
      console.log("💾 Saving transcription results for patient:", patientId);

      const supabase = await getSupabase();

      // Ensure credit_usage is integer for database compatibility
      let creditUsage = results.credit_usage || 0;
      if (typeof creditUsage === "string") {
        creditUsage = parseFloat(creditUsage);
      }
      if (typeof creditUsage === "number" && !Number.isInteger(creditUsage)) {
        creditUsage = Math.round(creditUsage);
      }
      creditUsage = parseInt(creditUsage) || 0;

      // Prepare data for storage
      const saveData = {
        patient_id: patientId,
        transcript: results.transcript,
        facts: results.facts,
        soap_notes: results.soap_notes,
        interaction_id: results.interaction_id,
        credit_usage: creditUsage, // Ensure integer type
        processing_method: results.processing_method,
        audio_storage_url: results.audio_storage_url,
        metadata: results.metadata,
        created_at: new Date().toISOString(),
      };

      // Save to transcription_results table (create if doesn't exist)
      const { data, error } = await supabase
        .from("transcription_results")
        .insert([saveData])
        .select()
        .single();

      if (error) {
        // If table doesn't exist, try to create it
        if (error.code === "42P01") {
          console.log(
            "🔧 Transcription results table not found, attempting to create..."
          );

          // Create table with proper schema
          await this._createTranscriptionResultsTable(supabase);

          // Retry the insert
          const { data: retryData, error: retryError } = await supabase
            .from("transcription_results")
            .insert([saveData])
            .select()
            .single();

          if (retryError) {
            throw retryError;
          }

          console.log(
            "✅ Transcription results saved successfully after table creation"
          );
          return { success: true, data: retryData };
        } else if (error.code === "22P02") {
          // Data type error (likely credit_usage)
          console.warn(
            "Database schema issue, attempting to fix credit_usage:",
            error
          );

          // Retry with explicit integer conversion
          saveData.credit_usage = Math.round(
            Number(saveData.credit_usage) || 0
          );

          const { data: retryData, error: retryError } = await supabase
            .from("transcription_results")
            .insert([saveData])
            .select()
            .single();

          if (retryError) {
            throw retryError;
          }

          return { success: true, data: retryData };
        } else {
          throw error;
        }
      }

      console.log("✅ Transcription results saved successfully");
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error saving transcription results:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Extract facts from transcript text
   * @param {array} transcript - Array of transcript segments
   * @returns {array} Extracted facts
   */
  async extractFactsFromTranscript(transcript) {
    try {
      if (!transcript || transcript.length === 0) {
        return [];
      }

      // Combine transcript text
      const fullText = transcript
        .map((segment) => segment.transcript || segment.text || "")
        .filter((text) => text.trim())
        .join(" ");

      if (!fullText.trim()) {
        return [];
      }

      // Simple fact extraction patterns (can be enhanced with AI/ML)
      const facts = [];

      // Medical symptoms patterns
      const symptomPatterns = [
        /(?:patient|he|she|they)\s+(?:reports?|experiences?|has|have)\s+([^.]+)/gi,
        /(?:complains? of|suffering from|feeling)\s+([^.]+)/gi,
      ];

      // Vital signs patterns
      const vitalPatterns = [
        /(?:blood pressure|BP)[:\s]+([^.]+)/gi,
        /(?:heart rate|HR|pulse)[:\s]+([^.]+)/gi,
        /(?:temperature|temp)[:\s]+([^.]+)/gi,
        /(?:weight|WT)[:\s]+([^.]+)/gi,
      ];

      // Medications patterns
      const medicationPatterns = [
        /(?:taking|prescribed|medication|medicine|drug)[:\s]+([^.]+)/gi,
      ];

      // Extract symptoms
      symptomPatterns.forEach((pattern) => {
        let match;
        while ((match = pattern.exec(fullText)) !== null) {
          facts.push({
            text: match[1].trim(),
            category: "symptom",
            confidence: 0.7,
            source: "pattern_match",
          });
        }
      });

      // Extract vital signs
      vitalPatterns.forEach((pattern) => {
        let match;
        while ((match = pattern.exec(fullText)) !== null) {
          facts.push({
            text: match[1].trim(),
            category: "vital_sign",
            confidence: 0.8,
            source: "pattern_match",
          });
        }
      });

      // Extract medications
      medicationPatterns.forEach((pattern) => {
        let match;
        while ((match = pattern.exec(fullText)) !== null) {
          facts.push({
            text: match[1].trim(),
            category: "medication",
            confidence: 0.75,
            source: "pattern_match",
          });
        }
      });

      // Remove duplicates and limit results
      const uniqueFacts = facts
        .filter(
          (fact, index, self) =>
            index ===
            self.findIndex(
              (f) => f.text.toLowerCase() === fact.text.toLowerCase()
            )
        )
        .slice(0, 20); // Limit to 20 facts

      console.log(`🔍 Extracted ${uniqueFacts.length} facts from transcript`);
      return uniqueFacts;
    } catch (error) {
      console.error("❌ Error extracting facts:", error);
      return [];
    }
  }

  /**
   * Generate SOAP notes from transcript and facts
   * @param {array} transcript - Array of transcript segments
   * @param {array} facts - Array of extracted facts
   * @returns {object} SOAP notes object
   */
  async generateSOAPNotes(transcript, facts) {
    try {
      // Combine transcript text
      const fullText = transcript
        .map((segment) => segment.transcript || segment.text || "")
        .filter((text) => text.trim())
        .join(" ");

      if (!fullText.trim() && (!facts || facts.length === 0)) {
        return {};
      }

      // Group facts by category
      const factsByCategory = facts.reduce((acc, fact) => {
        if (!acc[fact.category]) {
          acc[fact.category] = [];
        }
        acc[fact.category].push(fact.text);
        return acc;
      }, {});

      // Generate SOAP notes sections from full transcript
      const transcriptSoapNotes = {
        // Subjective - Patient's reported symptoms and concerns (from full transcript)
        subjective: this._generateSubjectiveSection(factsByCategory, fullText),

        // Objective - Observable data and vital signs (from full transcript)
        objective: this._generateObjectiveSection(factsByCategory, fullText),

        // Assessment - Clinical impression (from full transcript)
        assessment: this._generateAssessmentSection(factsByCategory, fullText),

        // Plan - Treatment recommendations (from full transcript)
        plan: this._generatePlanSection(factsByCategory, fullText),
      };

      // Generate SOAP notes sections from facts only
      const factsSoapNotes = {
        // Facts-based sections should be more concise and objective
        subjective: null,

        // Objective - Based only on extracted factual data
        objective: this._generateFactsOnlyObjectiveSection(factsByCategory),

        // Assessment - Based only on extracted facts
        assessment: this._generateFactsOnlyAssessmentSection(factsByCategory),

        // Plan - Based only on extracted facts
        plan: this._generateFactsOnlyPlanSection(factsByCategory),
      };

      // Generate combined SOAP notes for compatibility
      const combinedSoapNotes = {
        transcript_only: this._generateCombinedSOAP(
          transcriptSoapNotes,
          "transcript"
        ),
        facts_only: this._generateCombinedSOAP(factsSoapNotes, "facts"),
      };

      console.log(
        "📝 Generated transcript-based SOAP notes:",
        transcriptSoapNotes
      );
      console.log("📝 Generated facts-based SOAP notes:", factsSoapNotes);
      return combinedSoapNotes;
    } catch (error) {
      console.error("❌ Error generating SOAP notes:", error);
      return {};
    }
  }

  /**
   * Handle transcription errors with enhanced user-friendly messages
   * @param {Error} error - Error object
   * @param {function} onProgress - Progress callback
   * @returns {object} Formatted error response
   */
  handleTranscriptionError(error, onProgress = null) {
    console.error("🚨 Handling transcription error:", error);

    this._logProgress(onProgress, "error", "Transcription failed");

    let userMessage = this._getUserFriendlyError(error.message);
    let errorType = "unknown";

    // Handle specific error types
    if (
      error.message.includes("no_speech_detected") ||
      error.message.includes("No speech detected")
    ) {
      userMessage =
        "No speech was detected in the audio. Please ensure the recording contains clear speech and try again.";
      errorType = "no_speech";
    } else if (error.message.includes("File too large")) {
      userMessage =
        "The audio file is too large. Please use a smaller file (maximum 150MB).";
      errorType = "file_too_large";
    } else if (error.message.includes("Invalid file type")) {
      userMessage =
        "The audio file format is not supported. Please use MP3, WAV, M4A, or WebM format.";
      errorType = "invalid_format";
    } else if (
      error.message.includes("credit") ||
      error.message.includes("limit")
    ) {
      userMessage =
        "Credit limit exceeded. Please check your account and try again later.";
      errorType = "credit_limit";
    } else if (
      error.message.includes("CORS") ||
      error.message.includes("blocked by CORS")
    ) {
      userMessage =
        "Browser security restrictions are blocking the transcription service. Please check your network settings or try a different browser.";
      errorType = "cors";
    }

    return {
      success: false,
      error: userMessage,
      errorType,
      originalError: error.message,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Enable or disable fallback mode
   * @param {boolean} enabled - Whether fallback should be enabled
   */
  setFallbackEnabled(enabled) {
    this.fallbackEnabled = enabled;
    console.log(`Fallback mode ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Get current service status
   * @returns {object} Service status information
   */
  getServiceStatus() {
    return {
      cortiAvailable: this.cortiAvailable,
      fallbackEnabled: this.fallbackEnabled,
      lastHealthCheck: new Date(this.lastHealthCheck).toISOString(),
      retryAttempts: this.retryAttempts,
      retryDelay: this.retryDelay,
    };
  }

  // Private helper methods

  _getBackendUrl() {
    // Get the correct backend URL based on environment
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return BASE_URLS.local.backend;
    }
    return BASE_URLS.render.backend;
  }

  _logProgress(onProgress, stage, message) {
    if (typeof onProgress === "function") {
      onProgress({
        stage,
        message,
        timestamp: new Date().toISOString(),
      });
    }
  }

  async _getAuthToken() {
    // Get auth token from Supabase
    const supabase = await getSupabase();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || "";
  }

  async _delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async _logTranscription({
    patientContext,
    audioUrl,
    result,
    error,
    processingTime,
    attempt = 1,
  }) {
    try {
      const logData = {
        patientId: patientContext.id,
        patientName:
          patientContext.name || patientContext[Object.keys(patientContext)[0]],
        transcriptionType: "recorded",
        audioFileName: audioUrl.split("/").pop() || "unknown",
        audioDurationSeconds: 0, // Would need to be calculated separately
        audioSource: "url",
        audioUrl: audioUrl,
        creditUsage: parseInt(result?.credit_usage || 0), // Ensure integer
        transcriptLength: parseInt(result?.transcript?.length || 0), // Ensure integer
        factsExtracted: parseInt(result?.facts?.length || 0), // Ensure integer
        soapNotesGenerated: result?.soap_notes
          ? Object.keys(result.soap_notes).length > 0
          : false,
        processingTimeSeconds: Math.round(processingTime || 0),
        status: error ? "failed" : "completed",
        errorMessage: error || null,
        metadata: {
          attempt,
          processing_method: result?.processing_method || "unknown",
          error_type: error ? "transcription_failed" : null,
        },
      };

      await cortiLogger.logTranscription(logData);
    } catch (logError) {
      console.warn("⚠️ Failed to log transcription:", logError);
    }
  }

  _cleanSoapNotes(soapNotes) {
    if (!soapNotes) return {};

    const cleaned = {};

    // Clean transcript_only - be more lenient with content validation
    if (soapNotes.transcript_only) {
      if (typeof soapNotes.transcript_only === "string") {
        const content = soapNotes.transcript_only.trim();

        // Only filter out if it's clearly JSON schema content (starts with { and contains "properties" and "type")
        const isJsonSchema =
          content.startsWith("{") &&
          content.includes('"properties"') &&
          content.includes('"type"') &&
          !content.includes("Subjective:") &&
          !content.includes("Objective:") &&
          !content.includes("Assessment:") &&
          !content.includes("Plan:");

        if (!isJsonSchema || content.length > 50) {
          cleaned.transcript_only = soapNotes.transcript_only;
          console.log(
            "✅ Kept transcript_only content:",
            content.substring(0, 100) + "..."
          );
        } else {
          console.log("🧹 Filtered out JSON schema transcript_only content");
        }
      }
    }

    // Clean facts_only - be more lenient with content validation
    if (soapNotes.facts_only) {
      if (typeof soapNotes.facts_only === "string") {
        const content = soapNotes.facts_only.trim();

        // Only filter out if it's clearly JSON schema content
        const isJsonSchema =
          content.startsWith("{") &&
          content.includes('"properties"') &&
          content.includes('"type"') &&
          !content.includes("Assessment:") &&
          !content.includes("Plan:");

        if (!isJsonSchema || content.length > 50) {
          cleaned.facts_only = soapNotes.facts_only;
          console.log(
            "✅ Kept facts_only content:",
            content.substring(0, 100) + "..."
          );
        } else {
          console.log("🧹 Filtered out JSON schema facts_only content");
        }
      }
    }

    console.log("🧹 SOAP notes cleaning result:", Object.keys(cleaned));
    return cleaned;
  }

  _validateTranscript(transcript) {
    if (!Array.isArray(transcript)) {
      return [];
    }

    return transcript
      .filter((segment) => {
        // Handle both 'transcript' and 'text' properties for compatibility
        const text = segment.transcript || segment.text || "";
        return text && text.trim().length > 0;
      })
      .map((segment) => ({
        // Always use 'transcript' property as the canonical field name
        transcript: segment.transcript || segment.text || "",
        // Keep the original field as well for backward compatibility
        text: segment.text || segment.transcript || "",
        speaker: segment.speaker || "unknown",
        timestamp: segment.timestamp || Date.now(),
        confidence: segment.confidence || 1.0,
      }));
  }

  /**
   * Create continuous transcript text from transcript segments
   * @param {array} transcript - Array of transcript segments
   * @returns {string} Continuous transcript text
   */
  _createContinuousTranscript(transcript) {
    console.log(
      "🔍 DEBUG - _createContinuousTranscript called with:",
      transcript
    );

    if (!Array.isArray(transcript) || transcript.length === 0) {
      console.log("⚠️ DEBUG - Empty or invalid transcript array");
      return "";
    }

    console.log(
      `📝 DEBUG - Processing ${transcript.length} transcript segments`
    );

    // Extract all transcript text in order, maintaining chronological sequence
    const transcriptTexts = transcript
      .map((segment, index) => {
        const text = segment.transcript || segment.text || "";
        // Clean up the text - remove extra whitespace and ensure proper punctuation
        const trimmedText = text.trim();
        console.log(`📝 DEBUG - Segment ${index}: "${trimmedText}"`);
        return trimmedText;
      })
      .filter((text) => text.length > 0);

    console.log(
      `📝 DEBUG - Filtered to ${transcriptTexts.length} non-empty segments`
    );

    // Join with proper spacing and punctuation handling
    let continuousText = "";
    transcriptTexts.forEach((text, index) => {
      // Add space before text if it doesn't start with punctuation
      if (index > 0 && !text.match(/^[.,!?;:]/)) {
        continuousText += " ";
      }
      continuousText += text;
    });

    // Clean up the final text - remove multiple spaces and ensure proper sentence ending
    const finalText = continuousText
      .replace(/\s+/g, " ") // Replace multiple spaces with single space
      .replace(/\s+([.,!?;:])/g, "$1") // Remove space before punctuation
      .trim();

    console.log(`📝 DEBUG - Final continuous transcript: "${finalText}"`);
    console.log(
      `📝 DEBUG - Final continuous transcript length: ${finalText.length}`
    );

    return finalText;
  }

  _generateSubjectiveSection(factsByCategory, fullText) {
    const symptoms = factsByCategory.symptom || [];
    const subjectiveTexts = [];

    if (symptoms.length > 0) {
      subjectiveTexts.push(`Patient reports: ${symptoms.join(", ")}`);
    }

    // Extract general statements from transcript
    const generalMatch = fullText.match(
      /(?:patient|he|she|they)\s+(?:feels?|reports?|says?)\s+([^.]+)/gi
    );
    if (generalMatch) {
      subjectiveTexts.push(
        ...generalMatch.slice(0, 3).map((match) => match.trim())
      );
    }

    return subjectiveTexts.join(" ") || "";
  }

  _generateObjectiveSection(factsByCategory, fullText) {
    const vitals = factsByCategory.vital_sign || [];
    const objectiveTexts = [];

    if (vitals.length > 0) {
      objectiveTexts.push(`Vital signs: ${vitals.join(", ")}`);
    }

    // Extract objective observations
    const obsMatch = fullText.match(
      /(?:observed|noted|measured|recorded)\s+([^.]+)/gi
    );
    if (obsMatch) {
      objectiveTexts.push(...obsMatch.slice(0, 2).map((match) => match.trim()));
    }

    return objectiveTexts.join(" ") || "";
  }

  _generateAssessmentSection(factsByCategory, fullText) {
    const assessments = [];

    // Look for assessment statements
    const assessmentMatch = fullText.match(
      /(?:assessment|impression|diagnosis)[:\s]+([^.]+)/gi
    );
    if (assessmentMatch) {
      assessments.push(...assessmentMatch.map((match) => match.trim()));
    }

    // Generate basic assessment based on symptoms
    const symptoms = factsByCategory.symptom || [];
    if (symptoms.length > 0) {
      assessments.push(
        `Based on reported symptoms: ${symptoms.slice(0, 3).join(", ")}`
      );
    }

    return assessments.join(" ") || "";
  }

  _generatePlanSection(factsByCategory, fullText) {
    const medications = factsByCategory.medication || [];
    const planTexts = [];

    if (medications.length > 0) {
      planTexts.push(`Medications: ${medications.join(", ")}`);
    }

    // Extract plan statements
    const planMatch = fullText.match(
      /(?:plan|treatment|recommend|prescribe)[:\s]+([^.]+)/gi
    );
    if (planMatch) {
      planTexts.push(...planMatch.map((match) => match.trim()));
    }

    // Add follow-up recommendation
    if (planTexts.length === 0) {
      planTexts.push("Follow-up as needed or if symptoms worsen.");
    }

    return planTexts.join(" ");
  }

  /**
   * Generate objective section based only on extracted facts
   * @param {object} factsByCategory - Facts grouped by category
   * @returns {string} Objective section text
   */
  _generateFactsOnlyObjectiveSection(factsByCategory) {
    const objectiveTexts = [];

    // Only use vital signs from facts
    const vitals = factsByCategory.vital_sign || [];
    if (vitals.length > 0) {
      objectiveTexts.push(`Vital signs documented:\n• ${vitals.join("\n• ")}`);
    }

    // Include any medication facts as objective data
    const medications = factsByCategory.medication || [];
    if (medications.length > 0) {
      objectiveTexts.push(`Current medications:\n• ${medications.join("\n• ")}`);
    }

    return objectiveTexts.join("\n\n") || "";
  }

  /**
   * Generate assessment section based only on extracted facts
   * @param {object} factsByCategory - Facts grouped by category
   * @returns {string} Assessment section text
   */
  _generateFactsOnlyAssessmentSection(factsByCategory) {
    const assessments = [];

    // Generate assessment based only on symptoms from facts
    const symptoms = factsByCategory.symptom || [];
    if (symptoms.length > 0) {
      assessments.push(
        `Clinical findings from extracted facts:\n• ${symptoms
          .slice(0, 5)
          .join("\n• ")}`
      );
    }

    // Include any specific assessment facts
    const assessmentFacts = factsByCategory.assessment || [];
    if (assessmentFacts.length > 0) {
      assessments.push(...assessmentFacts);
    }

    // Include general facts as well to ensure all extracted data is represented
    const generalFacts = factsByCategory.general || [];
    if (generalFacts.length > 0) {
      assessments.push(`General findings:\n• ${generalFacts.join("\n• ")}`);
    }

    return assessments.join("\n\n") || "";
  }

  /**
   * Generate plan section based only on extracted facts
   * @param {object} factsByCategory - Facts grouped by category
   * @returns {string} Plan section text
   */
  _generateFactsOnlyPlanSection(factsByCategory) {
    const planTexts = [];

    // Include medications from facts
    const medications = factsByCategory.medication || [];
    if (medications.length > 0) {
      planTexts.push(`Medication management:\n• ${medications.join("\n• ")}`);
    }

    // Include any plan facts
    const planFacts = factsByCategory.plan || [];
    if (planFacts.length > 0) {
      planTexts.push(...planFacts);
    }

    return planTexts.join("\n\n");
  }

  _generateCombinedSOAP(soapNotes, source = "transcript") {
    if (source === "transcript") {
      return `Subjective:
${soapNotes.subjective || "--"}

Objective:
${soapNotes.objective || "--"}

Assessment:
${soapNotes.assessment || "--"}

Plan:
${soapNotes.plan || "--"}`;
    } else {
      return `Subjective:
${soapNotes.subjective || "--"}

Objective:
${soapNotes.objective || "--"}

Assessment:
${soapNotes.assessment || "--"}

Plan:
${soapNotes.plan || "--"}`;
    }
  }

  async _createTranscriptionResultsTable(supabase) {
    try {
      // Create transcription_results table with proper schema
      const createTableSQL = `
        CREATE TABLE IF NOT EXISTS transcription_results (
          id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
          patient_id TEXT NOT NULL,
          transcript JSONB,
          facts JSONB,
          soap_notes JSONB,
          interaction_id TEXT,
          credit_usage INTEGER, -- Changed from DECIMAL to INTEGER
          processing_method TEXT,
          audio_storage_url TEXT,
          metadata JSONB,
          created_at TIMESTAMPTZ DEFAULT NOW(),
          updated_at TIMESTAMPTZ DEFAULT NOW()
        );
        
        CREATE INDEX IF NOT EXISTS idx_transcription_results_patient_id ON transcription_results(patient_id);
        CREATE INDEX IF NOT EXISTS idx_transcription_results_created_at ON transcription_results(created_at);
        
        -- Add RLS policies if needed
        ALTER TABLE transcription_results ENABLE ROW LEVEL SECURITY;
        
        -- Allow authenticated users to read their own data
        CREATE POLICY IF NOT EXISTS "Users can view their own transcription results" 
        ON transcription_results FOR SELECT 
        USING (auth.uid()::text = patient_id);
        
        -- Allow authenticated users to insert their own data
        CREATE POLICY IF NOT EXISTS "Users can insert their own transcription results" 
        ON transcription_results FOR INSERT 
        WITH CHECK (auth.uid()::text = patient_id);
      `;

      // Execute the SQL (this would need to be done via Supabase RPC or admin panel)
      console.log("📋 Creating transcription results table with proper schema");

      // Note: This SQL needs to be executed manually or via a migration
      // For now, we'll just log that the table creation was attempted
      return true;
    } catch (error) {
      console.error("Failed to create transcription results table:", error);
      return false;
    }
  }
}

// Export singleton instance
export const cortiIntegrationService = new CortiIntegrationService();
export default cortiIntegrationService;
